import { Component, Event, EventEmitter, h, Listen, Prop, State, Watch } from '@stencil/core';

@Component({
  tag: 'biel-search-body',
  styleUrl: 'biel-search-body.css',
  shadow: true,
})
export class BielSearchBody {
  @Prop() searchResults: { title: string; fragment: string; url: string }[] = [];
  @Prop() searchQuery: string;
  @Prop() lastSearchQuery: string;
  @Prop() aiResultFragment: string = 'Ask AI to answer your question';

  @State() selectedIndex: number = 0;
  @State() isHovering: boolean = false;
  @State() resultItems: HTMLElement[] = [];
  @State() isLoading: boolean = false;
  @State() hasSearched: boolean = false;

  @Event() resultClicked: EventEmitter<{ url: string }>;

  @Watch('searchResults')
  watchSearchResults() {
    // When search results change, we can mark search as completed
    if (this.isLoading) {
      this.isLoading = false;
      this.hasSearched = true;
    }
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (this.searchResults && this.searchResults.length > 0) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.navigateDown();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.navigateUp();
          break;
        case 'Enter':
          event.preventDefault();
          this.selectResult();
          break;
      }
    }
  }

  @Listen('inputChanged', { target: 'body' })
  handleInputChanged() {
    this.isLoading = true;
    this.hasSearched = false;
  }

  navigateDown() {
    if (this.selectedIndex < this.searchResults.length) {
      this.selectedIndex += 1;
      this.scrollToSelected();
    }
  }

  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex -= 1;
      this.scrollToSelected();
    }
  }

  selectResult() {
    if (this.selectedIndex !== 0) {
      const selectedResult = this.searchResults[this.selectedIndex - 1];
      if (selectedResult) {
        this.resultClick(selectedResult.url);
      }
    }
  }

  handleMouseEnter(index: number) {
    this.selectedIndex = index;
    this.isHovering = true;
  }

  handleMouseLeave() {
    this.selectedIndex = -1;
    this.isHovering = false;
  }

  scrollToSelected() {
    const selectedElement = this.resultItems[this.selectedIndex];
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  resultClick(url: string) {
    this.resultClicked.emit({ url: url });
  }

  render() {
    const showLoadingAnimation = this.isLoading;
    const showNoResults =
      this.hasSearched &&
      !this.isLoading &&
      this.searchResults &&
      this.searchResults.length === 0 &&
      this.searchQuery.length > 3;

    return (
      <div class="biel-search-body-container" onMouseLeave={() => this.handleMouseLeave()}>
        {this.searchQuery !== '' && (
          <div>
            <ul class="biel-search-body__result-list">
              <li
                class="biel-search-body__result-item"
                onMouseMove={() => this.handleMouseEnter(0)}
                ref={(el) => (this.resultItems[0] = el as HTMLElement)}
              >
                <biel-search-ask-ai-button
                  class="biel-search-body__result"
                  resultTitle={this.searchQuery}
                  resultFragment={this.aiResultFragment}
                  isSelected={this.selectedIndex === 0}
                ></biel-search-ask-ai-button>
              </li>
              {this.searchResults &&
                this.searchResults.length > 0 &&
                this.searchResults.map((result, index) => (
                  <li
                    class="biel-search-body__result-item"
                    onMouseMove={() => this.handleMouseEnter(index + 1)}
                    ref={(el) => (this.resultItems[index + 1] = el as HTMLElement)}
                  >
                    <biel-search-result
                      class="biel-search-body__result"
                      onClick={() => this.resultClick(result.url)}
                      resultTitle={result.title}
                      resultFragment={result.fragment}
                      resultUrl={result.url}
                      searchQuery={this.searchQuery}
                      isSelected={this.selectedIndex === index + 1}
                    ></biel-search-result>
                  </li>
                ))}
            </ul>
            {showLoadingAnimation && (
              <div class="loading-animation">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
            )}
            {showNoResults && (
              <p class="biel-search-body-no-results">Nothing found for "{this.searchQuery}"</p>
            )}
          </div>
        )}
      </div>
    );
  }
}
